import {
  ActionFactory,
  ActionNames,
  ContextBase as SystemActionContext,
  EditActionBase as SystemAction,
  EditActionDefinition,
  HostEditAction,
  NativeActionsList,
  SystemActionNames,
} from '@wix/editor-platform-host-integration-apis';
import { Actions } from '@wix/component-protocol/schema';

import { createActionPanelAPI } from '../nativeActions/actionPanelAPI';
import { createCustomisationAPI } from '../customActions/customisationAPI';
import {
  ActionFactoryWithPlatformContext,
  ContributedEditActionsDict,
  ContributedPlatformActionsDict,
  PlatformContext,
  PlatformEditAction,
} from '../types';
import { shouldBeExcludedFromEditActions } from './helpers';
import { ActionContextAPI } from '../nativeActions/ActionContextAPI';
import {
  createEditorPlatformEditActionsError,
  EditorPlatformEditActionsErrorCode,
} from '../errors';

const isActionFactory = <TContext, Type extends EditActionDefinition>(
  action: EditActionDefinition | ActionFactory<TContext, Type>,
): action is ActionFactory<TContext, Type> => typeof action === 'function';

const actionsResolver = <TAction extends EditActionDefinition, TContext>(
  action: TAction | ActionFactory<TContext, TAction>,
  context: TContext,
): TAction => (isActionFactory(action) ? action(context) : action);

export const resolveSystemActionsWithFactories = <
  TActionDefinition extends EditActionDefinition = EditActionDefinition,
>(
  actions: SystemAction<SystemActionNames>[],
  context: PlatformContext,
): TActionDefinition[] => {
  return actions
    .map((action) =>
      actionsResolver<SystemAction, SystemActionContext>(action, context),
    )
    .filter((action) => {
      if (!action) {
        return false;
      }

      return !shouldBeExcludedFromEditActions(action);
    }) as TActionDefinition[];
};

export const resolveCustomActionsWithFactories = <
  TActionDefinition extends EditActionDefinition = EditActionDefinition,
>(
  actions: Record<string, ActionFactoryWithPlatformContext>,
  manifestActions: Actions,
  context: PlatformContext,
): TActionDefinition[] => {
  return Object.entries(manifestActions).flatMap(([actionName, action]) => {
    const actionPanelAPI = createActionPanelAPI(action, actionName, context);
    const customisationAPI = createCustomisationAPI(
      action,
      actionName,
      context,
    );
    const isValidExecution =
      'isValidExecution' in customisationAPI
        ? customisationAPI?.isValidExecution?.() ?? true
        : true;

    if (isValidExecution) {
      const resolvedAction = actionsResolver(
        actions[actionName] as PlatformEditAction,
        {
          ...context,
          customisationAPI,
          actionPanelAPI,
        },
      ) as TActionDefinition;
      return [resolvedAction];
    } else {
      console.error(
        createEditorPlatformEditActionsError(
          EditorPlatformEditActionsErrorCode.EditActionExecutionIsInvalid,
          `Missing execution data or  for action ${actionName}`,
        ).withActionName(actionName),
      );
      return [];
    }
  });
};

export const resolvePlatformActionsWithFactories = <
  TActionDefinition extends EditActionDefinition = EditActionDefinition,
>(
  actions: ContributedPlatformActionsDict,
  manifestActions: Actions,
  context: PlatformContext,
): TActionDefinition[] => {
  return Object.entries(manifestActions)
    .map(([actionName, action]) => {
      const actionPanelAPI = createActionPanelAPI(action, actionName, context);
      const contextAPI = new ActionContextAPI(
        action,
        actionName as keyof typeof NativeActionsList,
        context,
      );
      const customisationAPI = createCustomisationAPI(
        action,
        actionName,
        context,
      );
      return actionsResolver(
        actions[actionName as ActionNames] as PlatformEditAction,
        {
          ...context,
          customisationAPI,
          actionPanelAPI,
          contextAPI,
        },
      );
    })
    .filter((action) => !!action) as TActionDefinition[];
};

export const resolveHostActionsWithFactories = <
  TActionDefinition extends EditActionDefinition = EditActionDefinition,
>(
  actions: ContributedEditActionsDict,
  manifestActions: Actions,
  context: PlatformContext,
): TActionDefinition[] => {
  return Object.entries(manifestActions)
    .map(([actionName, action]) => {
      const actionPanelAPI = createActionPanelAPI(action, actionName, context);
      const customisationAPI = createCustomisationAPI(
        action,
        actionName,
        context,
      );
      const contextAPI = new ActionContextAPI(
        action,
        actionName as keyof typeof NativeActionsList,
        context,
      );
      return actionsResolver(
        actions[actionName as ActionNames] as HostEditAction,
        {
          editorPointer: context.editorPointer,
          customisationAPI,
          actionPanelAPI,
          contextAPI,
        },
      );
    })
    .filter((action) => {
      if (!action) {
        return false;
      }

      return !shouldBeExcludedFromEditActions(action);
    }) as TActionDefinition[];
};
